// ==================== SUBSCRIPTION PLANS PAGE (ADMIN) ====================

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  Edit2, 
  Check, 
  Crown,
  Users,
  MessageSquare,
  FileText,
  Send,
  DollarSign,
  ToggleLeft,
  ToggleRight,
  X,
  Loader2,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { Select } from '../../components/ui/Select';
import { Toggle } from '../../components/ui/Toggle';
import { Badge } from '../../components/ui/Badge';
import { Card } from '../../components/ui/Card';
import { cn } from '../../utils/helpers';
import { fadeInUp, staggerContainer } from '../../utils/animations';
import { adminPlansService, type PlanWithCount, type CreatePlanDto, type UpdatePlanDto } from '../../services';

// ==================== FORM TYPES & VALIDATION ====================

const PLAN_TYPES = ['STARTER', 'STANDARD', 'PROFESSIONAL', 'ENTERPRISE'] as const;

// Define the form data type explicitly
interface PlanFormData {
  name: 'STARTER' | 'STANDARD' | 'PROFESSIONAL' | 'ENTERPRISE';
  description?: string;
  monthlyPrice: number;
  yearlyPrice?: number;
  maxContacts: number;
  maxMessagesPerMonth: number;
  maxCampaigns: number;
  maxTemplates: number;
  features?: string;
  isActive?: boolean;
  isPopular?: boolean;
  sortOrder?: number;
}

const planFormSchema = z.object({
  name: z.enum(PLAN_TYPES, { message: 'Plan type is required' }),
  description: z.string().max(500).optional(),
  monthlyPrice: z.number().min(0, 'Price must be 0 or greater'),
  yearlyPrice: z.number().min(0).optional(),
  maxContacts: z.number().int(),
  maxMessagesPerMonth: z.number().int(),
  maxCampaigns: z.number().int(),
  maxTemplates: z.number().int(),
  features: z.string().optional(),
  isActive: z.boolean().optional(),
  isPopular: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
});

// ==================== PLAN MODAL COMPONENT ====================

interface PlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  plan?: PlanWithCount | null;
  onSuccess: () => void;
}

const PlanModal = ({ isOpen, onClose, plan, onSuccess }: PlanModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isEditing = !!plan;

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<PlanFormData>({
    resolver: zodResolver(planFormSchema),
    defaultValues: {
      name: 'STARTER',
      description: '',
      monthlyPrice: 0,
      yearlyPrice: 0,
      maxContacts: 1000,
      maxMessagesPerMonth: 5000,
      maxCampaigns: 5,
      maxTemplates: 10,
      features: '',
      isActive: true,
      isPopular: false,
      sortOrder: 0,
    },
  });

  const isActive = watch('isActive');
  const isPopular = watch('isPopular');

  // Reset form when modal opens/closes or plan changes
  useEffect(() => {
    if (isOpen && plan) {
      reset({
        name: plan.name,
        description: plan.description || '',
        monthlyPrice: typeof plan.monthlyPrice === 'string' ? parseFloat(plan.monthlyPrice) : plan.monthlyPrice,
        yearlyPrice: plan.yearlyPrice ? (typeof plan.yearlyPrice === 'string' ? parseFloat(plan.yearlyPrice) : plan.yearlyPrice) : 0,
        maxContacts: plan.maxContacts,
        maxMessagesPerMonth: plan.maxMessagesPerMonth,
        maxCampaigns: plan.maxCampaigns,
        maxTemplates: plan.maxTemplates,
        features: plan.features?.join('\n') || '',
        isActive: plan.isActive ?? true,
        isPopular: plan.isPopular ?? false,
        sortOrder: plan.sortOrder ?? 0,
      });
    } else if (isOpen && !plan) {
      reset({
        name: 'STARTER',
        description: '',
        monthlyPrice: 0,
        yearlyPrice: 0,
        maxContacts: 1000,
        maxMessagesPerMonth: 5000,
        maxCampaigns: 5,
        maxTemplates: 10,
        features: '',
        isActive: true,
        isPopular: false,
        sortOrder: 0,
      });
    }
  }, [isOpen, plan, reset]);

  const onSubmit = async (data: PlanFormData) => {
    setIsSubmitting(true);
    setError(null);

    const featuresArray = data.features
      ? data.features.split('\n').map(f => f.trim()).filter(f => f.length > 0)
      : [];

    const payload: CreatePlanDto | UpdatePlanDto = {
      name: data.name,
      description: data.description || undefined,
      monthlyPrice: data.monthlyPrice,
      yearlyPrice: data.yearlyPrice || undefined,
      maxContacts: data.maxContacts,
      maxMessagesPerMonth: data.maxMessagesPerMonth,
      maxCampaigns: data.maxCampaigns,
      maxTemplates: data.maxTemplates,
      features: featuresArray,
      isActive: data.isActive,
      isPopular: data.isPopular,
      sortOrder: data.sortOrder,
    };

    try {
      const response = isEditing
        ? await adminPlansService.update(plan.id, payload)
        : await adminPlansService.create(payload as CreatePlanDto);

      if (response.success) {
        onSuccess();
        onClose();
      } else {
        setError(response.message || 'Failed to save plan');
      }
    } catch {
      setError('Failed to save plan');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-4 bg-(--color-charcoal-black) border border-(--color-border-gray) rounded-xl shadow-2xl"
      >
        {/* Header */}
        <div className="sticky top-0 flex items-center justify-between p-6 border-b border-(--color-border-gray) bg-(--color-charcoal-black)">
          <h2 className="text-xl font-heading font-bold text-white">
            {isEditing ? 'Edit Plan' : 'Create New Plan'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-(--color-muted-gray) hover:text-white hover:bg-white/5 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          {error && (
            <div className="flex items-center gap-3 p-4 rounded-lg bg-(--color-error)/10 border border-(--color-error)/30">
              <AlertCircle size={20} className="text-(--color-error)" />
              <p className="text-sm text-(--color-error)">{error}</p>
            </div>
          )}

          {/* Plan Type */}
          <div>
            <label className="block text-sm font-medium text-(--color-soft-gray) mb-2">
              Plan Type *
            </label>
            <Select
              options={[
                { value: 'STARTER', label: 'Starter' },
                { value: 'STANDARD', label: 'Standard' },
                { value: 'PROFESSIONAL', label: 'Professional' },
                { value: 'ENTERPRISE', label: 'Enterprise' },
              ]}
              value={watch('name')}
              onChange={(value) => setValue('name', value as PlanFormData['name'])}
              disabled={isEditing}
              error={errors.name?.message}
            />
          </div>

          {/* Description */}
          <div>
            <Textarea
              label="Description"
              placeholder="Brief description of the plan..."
              {...register('description')}
              error={errors.description?.message}
              rows={2}
            />
          </div>

          {/* Pricing */}
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Monthly Price ($) *"
              type="number"
              step="0.01"
              min="0"
              leftIcon={<DollarSign size={16} />}
              {...register('monthlyPrice', { valueAsNumber: true })}
              error={errors.monthlyPrice?.message}
            />
            <Input
              label="Yearly Price ($)"
              type="number"
              step="0.01"
              min="0"
              leftIcon={<DollarSign size={16} />}
              {...register('yearlyPrice', { valueAsNumber: true })}
              error={errors.yearlyPrice?.message}
            />
          </div>

          {/* Limits */}
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Max Contacts *"
              type="number"
              leftIcon={<Users size={16} />}
              {...register('maxContacts', { valueAsNumber: true })}
              error={errors.maxContacts?.message}
            />
            <Input
              label="Max Messages/Month *"
              type="number"
              leftIcon={<MessageSquare size={16} />}
              {...register('maxMessagesPerMonth', { valueAsNumber: true })}
              error={errors.maxMessagesPerMonth?.message}
            />
            <Input
              label="Max Templates *"
              type="number"
              leftIcon={<FileText size={16} />}
              {...register('maxTemplates', { valueAsNumber: true })}
              error={errors.maxTemplates?.message}
            />
            <Input
              label="Max Campaigns *"
              type="number"
              leftIcon={<Send size={16} />}
              {...register('maxCampaigns', { valueAsNumber: true })}
              error={errors.maxCampaigns?.message}
            />
          </div>

          {/* Features */}
          <div>
            <Textarea
              label="Features (one per line)"
              placeholder="Priority support&#10;Advanced analytics&#10;API access"
              {...register('features')}
              error={errors.features?.message}
              rows={4}
            />
          </div>

          {/* Options */}
          <div className="flex items-center gap-6">
            <Toggle
              label="Active"
              checked={isActive}
              onChange={(e) => setValue('isActive', e.target.checked)}
            />
            <Toggle
              label="Popular"
              checked={isPopular}
              onChange={(e) => setValue('isPopular', e.target.checked)}
            />
            <Input
              label="Sort Order"
              type="number"
              className="w-24"
              {...register('sortOrder', { valueAsNumber: true })}
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-(--color-border-gray)">
            <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" isLoading={isSubmitting}>
              {isEditing ? 'Save Changes' : 'Create Plan'}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// ==================== PLAN CARD COMPONENT ====================

interface PlanCardProps {
  plan: PlanWithCount;
  onEdit: (plan: PlanWithCount) => void;
  onToggleActive: (id: string) => void;
  isToggling: boolean;
}

const PlanCard = ({ plan, onEdit, onToggleActive, isToggling }: PlanCardProps) => {
  const monthlyPrice = typeof plan.monthlyPrice === 'string' ? parseFloat(plan.monthlyPrice) : plan.monthlyPrice;
  const yearlyPrice = plan.yearlyPrice ? (typeof plan.yearlyPrice === 'string' ? parseFloat(plan.yearlyPrice) : plan.yearlyPrice) : null;

  return (
    <motion.div variants={fadeInUp}>
      <Card className={cn(
        'relative h-full',
        !plan.isActive && 'opacity-60'
      )}>
        {/* Popular Badge */}
        {plan.isPopular && (
          <div className="absolute -top-3 left-4">
            <Badge variant="orange">
              <Crown size={12} className="mr-1" />
              Popular
            </Badge>
          </div>
        )}

        {/* Header */}
        <div className="flex items-start justify-between mb-4 pt-2">
          <div>
            <h3 className="text-xl font-heading font-bold text-white">{plan.name}</h3>
            {plan.description && (
              <p className="text-sm text-(--color-muted-gray) mt-1">{plan.description}</p>
            )}
            {plan._count && (
              <p className="text-xs text-(--color-muted-gray) mt-2">
                {plan._count.subscriptions} active subscription(s)
              </p>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => onToggleActive(plan.id)}
              disabled={isToggling}
              className="p-2 rounded-lg text-(--color-muted-gray) hover:text-white hover:bg-white/5 transition-colors disabled:opacity-50"
              title={plan.isActive ? 'Deactivate' : 'Activate'}
            >
              {isToggling ? (
                <Loader2 size={20} className="animate-spin" />
              ) : plan.isActive ? (
                <ToggleRight size={20} className="text-(--color-success)" />
              ) : (
                <ToggleLeft size={20} />
              )}
            </button>
            <button
              onClick={() => onEdit(plan)}
              className="p-2 rounded-lg text-(--color-muted-gray) hover:text-(--color-signal-orange) hover:bg-white/5 transition-colors"
              title="Edit plan"
            >
              <Edit2 size={18} />
            </button>
          </div>
        </div>

        {/* Pricing */}
        <div className="mb-6 p-4 rounded-xl bg-(--color-navy-light) border border-(--color-border-gray)">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign size={16} className="text-(--color-signal-orange)" />
            <span className="text-sm text-(--color-muted-gray)">Pricing</span>
          </div>
          <div className="flex items-baseline gap-4">
            <div>
              <span className="text-2xl font-bold text-white">${monthlyPrice}</span>
              <span className="text-(--color-muted-gray)">/mo</span>
            </div>
            {yearlyPrice && (
              <div className="text-sm text-(--color-soft-gray)">
                ${yearlyPrice}/year
              </div>
            )}
          </div>
        </div>

        {/* Limits */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <div className="p-3 rounded-lg bg-(--color-midnight-navy) border border-(--color-border-gray)">
            <div className="flex items-center gap-2 mb-1">
              <Users size={14} className="text-(--color-signal-orange)" />
              <span className="text-xs text-(--color-muted-gray)">Contacts</span>
            </div>
            <p className="text-lg font-semibold text-white">
              {plan.maxContacts === -1 ? 'Unlimited' : plan.maxContacts.toLocaleString()}
            </p>
          </div>
          <div className="p-3 rounded-lg bg-(--color-midnight-navy) border border-(--color-border-gray)">
            <div className="flex items-center gap-2 mb-1">
              <MessageSquare size={14} className="text-(--color-signal-orange)" />
              <span className="text-xs text-(--color-muted-gray)">Messages/mo</span>
            </div>
            <p className="text-lg font-semibold text-white">
              {plan.maxMessagesPerMonth === -1 ? 'Unlimited' : plan.maxMessagesPerMonth.toLocaleString()}
            </p>
          </div>
          <div className="p-3 rounded-lg bg-(--color-midnight-navy) border border-(--color-border-gray)">
            <div className="flex items-center gap-2 mb-1">
              <FileText size={14} className="text-(--color-signal-orange)" />
              <span className="text-xs text-(--color-muted-gray)">Templates</span>
            </div>
            <p className="text-lg font-semibold text-white">
              {plan.maxTemplates === -1 ? 'Unlimited' : plan.maxTemplates.toLocaleString()}
            </p>
          </div>
          <div className="p-3 rounded-lg bg-(--color-midnight-navy) border border-(--color-border-gray)">
            <div className="flex items-center gap-2 mb-1">
              <Send size={14} className="text-(--color-signal-orange)" />
              <span className="text-xs text-(--color-muted-gray)">Campaigns</span>
            </div>
            <p className="text-lg font-semibold text-white">
              {plan.maxCampaigns === -1 ? 'Unlimited' : plan.maxCampaigns.toLocaleString()}
            </p>
          </div>
        </div>

        {/* Features */}
        {plan.features && plan.features.length > 0 && (
          <div>
            <p className="text-sm font-medium text-white mb-3">Features</p>
            <ul className="space-y-2">
              {plan.features.map((feature, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-(--color-soft-gray)">
                  <Check size={14} className="text-(--color-success) shrink-0 mt-0.5" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Card>
    </motion.div>
  );
};

// ==================== MAIN COMPONENT ====================

const SubscriptionPlansPage = () => {
  const [plans, setPlans] = useState<PlanWithCount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<PlanWithCount | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  // Fetch plans
  const fetchPlans = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await adminPlansService.getAll(true);
      
      // Extract plans data from response - handle multiple structures
      let plansData: PlanWithCount[] = [];
      
      // Check if response itself is an array (direct data)
      if (Array.isArray(response)) {
        plansData = response as PlanWithCount[];
      }
      // Check if response.data is an array
      else if (response && Array.isArray(response.data)) {
        plansData = response.data;
      }
      // Check for nested data.data structure (when API wraps response in extra layer)
      else if (response && response.data && !Array.isArray(response.data) && typeof response.data === 'object') {
        const nested = response.data as unknown as { data?: PlanWithCount[] };
        if (nested.data && Array.isArray(nested.data)) {
          plansData = nested.data;
        }
      }
      
      if (plansData.length > 0) {
        setPlans(plansData);
      } else if (response && response.success === false) {
        setError(response.message || 'Failed to load plans');
        setPlans([]);
      } else {
        setPlans([]);
      }
    } catch {
      setError('Failed to load plans');
      setPlans([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const handleEdit = (plan: PlanWithCount) => {
    setEditingPlan(plan);
    setIsModalOpen(true);
  };

  const handleToggleActive = async (id: string) => {
    setTogglingId(id);
    try {
      const response = await adminPlansService.toggleActive(id);
      if (response.success) {
        await fetchPlans();
      } else {
        setError(response.message || 'Failed to toggle plan status');
      }
    } catch {
      setError('Failed to toggle plan status');
    } finally {
      setTogglingId(null);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingPlan(null);
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
      className="space-y-6"
    >
      {/* Page Header */}
      <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-heading font-bold text-white">Subscription Plans</h1>
          <p className="text-(--color-muted-gray) mt-1">
            Manage pricing plans and features
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={fetchPlans}
          leftIcon={<RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />}
          disabled={isLoading}
        >
          Refresh
        </Button>
      </motion.div>

      {/* Error Alert */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center gap-3 p-4 rounded-xl bg-(--color-error)/10 border border-(--color-error)/30"
          >
            <AlertCircle size={20} className="text-(--color-error)" />
            <p className="text-sm text-(--color-error) flex-1">{error}</p>
            <Button variant="ghost" size="sm" onClick={() => setError(null)}>
              Dismiss
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Plans Grid */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 size={32} className="text-(--color-signal-orange) animate-spin" />
        </div>
      ) : !plans || plans.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <DollarSign size={48} className="text-(--color-muted-gray) mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">No Plans Available</h3>
            <p className="text-(--color-muted-gray)">
              No subscription plans have been configured yet.
            </p>
          </div>
        </Card>
      ) : (
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="grid md:grid-cols-2 xl:grid-cols-4 gap-6"
        >
          {plans.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              onEdit={handleEdit}
              onToggleActive={handleToggleActive}
              isToggling={togglingId === plan.id}
            />
          ))}
        </motion.div>
      )}

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <PlanModal
            isOpen={isModalOpen}
            onClose={handleModalClose}
            plan={editingPlan}
            onSuccess={fetchPlans}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export { SubscriptionPlansPage };
